import {
  Modal,
  Form,
  Card,
  Row,
  Col,
  Input,
  DatePicker,
  message,
  Select,
} from "antd";
import dayjs from "dayjs";
import { useState, useEffect, useMemo } from "react";
import debounce from "lodash/debounce";

// ===== IMPORT YOUR API HERE =====
import {
  createAmc,
  updateAmc,
  getCustomersForReceipts,
  getallProjectForReceipts,
} from "../../utils/API";

interface AmcMaintenanceProps {
  visible: boolean;
  editData?: any;
  onClose: () => void;
  onSuccess: () => void;
}

const AmcMaintenanceModal: React.FC<AmcMaintenanceProps> = ({
  visible,
  editData,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  // States for dropdowns
  const [customers, setCustomers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  console.log("Projects in state:", projects);

  // States for loading dropdowns
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // States for search
  const [customerSearchText, setCustomerSearchText] = useState("");
  const [projectSearchText, setProjectSearchText] = useState("");
  const CUSTOM_VALUE_PREFIX = "__custom__:";

  // Reusable label style
  const labelStyle = {
    color: "#4b5563",
    fontFamily: "inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    paddingLeft: "15px",
  };

  // ===== SEARCH CUSTOMERS WITH DEBOUNCE =====
  const searchCustomers = async (searchText: string) => {
    setLoadingCustomers(true);
    try {
      // Pass search text to API
      const custRes: any = await getCustomersForReceipts(searchText, "", true);
      if (custRes?.data) {
        setCustomers(custRes.data.filter((c: any) => c.isActive));
      }
    } catch (error) {
      console.error("Failed to search customers", error);
    } finally {
      setLoadingCustomers(false);
    }
  };

  // ===== SEARCH PROJECTS WITH DEBOUNCE =====
  const searchProjects = async (searchText: string) => {
    setLoadingProjects(true);
    try {
      // Pass search text to API
      const projRes: any = await getallProjectForReceipts(searchText, "", true);
      if (projRes?.data) {
        setProjects(projRes.data.filter((p: any) => p.isActive));
      }
    } catch (error) {
      console.error("Failed to search projects", error);
    } finally {
      setLoadingProjects(false);
    }
  };

  // Debounced search functions (stable across renders)
  const debouncedSearchCustomers = useMemo(
    () => debounce(searchCustomers, 500),
    [],
  );
  const debouncedSearchProjects = useMemo(
    () => debounce(searchProjects, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearchCustomers.cancel();
      debouncedSearchProjects.cancel();
    };
  }, [debouncedSearchCustomers, debouncedSearchProjects]);

  // ===== FETCH INITIAL DATA ON MOUNT =====
  useEffect(() => {
    if (visible) {
      searchCustomers("");
      searchProjects("");
    }
  }, [visible]);
  console.log("editData: ", editData);

  // Reset or populate form when modal opens
  useEffect(() => {
    if (visible && editData) {
      // ===== EDIT MODE =====
      // Parse dates with correct format
      let startDate = null;
      let endDate = null;

      if (editData.start_date) {
        startDate = dayjs(editData.start_date, "DD-MM-YYYY");
      }

      if (editData.end_date) {
        endDate = dayjs(editData.end_date, "DD-MM-YYYY");
      }

      const initialProject =
        editData.project_name && typeof editData.project_name === "object"
          ? editData.project_name._id
          : editData.project_name;

      const initialCustomer =
        editData.customer_name && typeof editData.customer_name === "object"
          ? editData.customer_name._id
          : editData.customer_name;

      form.setFieldsValue({
        project_name: initialProject,
        customer_name: initialCustomer,
        customer_contact_number: editData.customer_contact_number,
        service_type: editData.service_type,
        start_date: startDate,
        end_date: endDate,
      });
    } else if (visible && !editData) {
      // ===== ADD MODE =====
      form.resetFields();
      setCustomerSearchText("");
      setProjectSearchText("");
    } else if (!visible) {
      setCustomerSearchText("");
      setProjectSearchText("");
    }
  }, [visible, editData, form]);

  const projectOptions = useMemo(() => {
    const list = projects.map((project: any) => ({
      label: project.projectName,
      value: project._id,
    }));

    if (editData && editData.project_name) {
      const editProj = editData.project_name;
      const isObj = typeof editProj === "object" && editProj !== null;
      const projId = isObj ? editProj._id : editProj;
      const projLabel = isObj ? editProj.projectName : editProj;

      const exists = list.some((item) => item.value === projId);
      if (!exists) {
        list.push({
          label: projLabel,
          value: projId,
        });
      }
    }

    const typed = projectSearchText.trim();
    const hasExactMatch = projects.some(
      (project: any) =>
        (project.projectName || "").toLowerCase() === typed.toLowerCase(),
    );

    if (typed && !hasExactMatch) {
      list.unshift({
        label: typed,
        value: `${CUSTOM_VALUE_PREFIX}${typed}`,
      });
    }

    return list;
  }, [projects, projectSearchText, editData]);

  const customerOptions = useMemo(() => {
    const list = customers.map((customer: any) => ({
      label: customer.name,
      value: customer._id,
    }));

    if (editData && editData.customer_name) {
      const editCust = editData.customer_name;
      const isObj = typeof editCust === "object" && editCust !== null;
      const custId = isObj ? editCust._id : editCust;
      const custLabel = isObj ? editCust.name : editCust;

      const exists = list.some((item) => item.value === custId);
      if (!exists) {
        list.push({
          label: custLabel,
          value: custId,
        });
      }
    }

    const typed = customerSearchText.trim();
    const hasExactMatch = customers.some(
      (customer: any) =>
        (customer.name || "").toLowerCase() === typed.toLowerCase(),
    );

    if (typed && !hasExactMatch) {
      list.unshift({
        label: typed,
        value: `${CUSTOM_VALUE_PREFIX}${typed}`,
      });
    }

    return list;
  }, [customers, customerSearchText, editData]);

  const onFinish = async (values: any) => {
    setIsLoading(true);

    try {
      const parsedProjectName =
        typeof values.project_name === "string" &&
        values.project_name.startsWith(CUSTOM_VALUE_PREFIX)
          ? values.project_name.replace(CUSTOM_VALUE_PREFIX, "")
          : values.project_name;

      const parsedCustomerName =
        typeof values.customer_name === "string" &&
        values.customer_name.startsWith(CUSTOM_VALUE_PREFIX)
          ? values.customer_name.replace(CUSTOM_VALUE_PREFIX, "")
          : values.customer_name;

      const payload = {
        project_name: parsedProjectName,
        customer_name: parsedCustomerName,
        customer_contact_number: values.customer_contact_number,
        service_type: values.service_type,
        start_date: values.start_date
          ? values.start_date.format("DD-MM-YYYY")
          : null,
        end_date: values.end_date ? values.end_date.format("DD-MM-YYYY") : null,
      };
      // console.log("Payload to save:", payload);

      if (editData) {
        await updateAmc(editData._id, payload);
        message.success("AMC Record Updated Successfully");
      } else {
        await createAmc(payload);
        message.success("AMC Record Added Successfully");
      }

      onSuccess();
      onClose();
    } catch (error) {
      message.error("Failed to save AMC record");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle customer search
  const handleCustomerSearch = (value: string) => {
    setCustomerSearchText(value);
    debouncedSearchCustomers(value);
  };

  // Handle project search
  const handleProjectSearch = (value: string) => {
    setProjectSearchText(value);
    debouncedSearchProjects(value);
  };

return (
    <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        width={800}
        centered={true}
        destroyOnClose
        className="custom-modal"
        bodyStyle={{
            overflowY: "auto",
            fontFamily: "inter, sans-serif",
        }}
        style={{
            bottom: 20,
            top: 20,
            margin: '0 auto',
            position: 'relative',
        }}
        styles={{
            header: {
                padding: "12px",
                position: "sticky",
                top: 0,
                backgroundColor: "#f1f5ffff",
            },
            content: {
                padding: 0,
                overflow: "hidden",
            },
        }}
        title={
            <div
                style={{
                    color: "#4b5563",
                    fontFamily: "inter, sans-serif",
                    fontSize: "20px",
                    fontWeight: 500,
                    display: "flex",
                }}
            >
                {editData ? "Edit AMC Maintenance" : "AMC Maintenance Details"}
            </div>
        }
    >
        <Form 
            form={form} 
            layout="horizontal"
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 19 }}
            colon={false} 
            style={{ margin: 0 }}
            onFinish={onFinish}
        >
            <div className="elements">
                <Form.Item
                    label={
                        <span style={{
                            color: '#4b5563',
                            fontFamily: 'inter, sans-serif',
                            fontSize: '14px',
                            paddingLeft: "15px",
                            fontWeight: 500,
                        }}>
                            Project <span className="text-red-500">*</span>
                        </span>
                    }
                    name="project_name"
                    required={false}
                    rules={[{ required: true, message: "Required" }]}
                >
                    <Select
                        size="large"
                        loading={loadingProjects}
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                            String(option?.label || "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        onSearch={handleProjectSearch}
                        placeholder="Search and Select Project"
                        className="CustomSelect"
                        options={projectOptions}
                        notFoundContent={
                            loadingProjects ? "Searching..." : "No projects found"
                        }
                        allowClear
                        onChange={(projectId) => {
                            const selectedProject = projects.find(
                                (p: any) => p._id === projectId
                            );

                            if (selectedProject?.clientId?._id) {
                                const customer = customers.find(
                                    (c: any) => c._id === selectedProject.clientId._id
                                );

                                form.setFieldsValue({
                                    customer_name: selectedProject.clientId.name,
                                    customer_contact_number: customer?.mobile || "",
                                });
                            }
                        }}
                    />
                </Form.Item>

                <Form.Item
                    label={
                        <span style={{
                            color: '#4b5563',
                            fontFamily: 'inter, sans-serif',
                            fontSize: '14px',
                            paddingLeft: "15px",
                            fontWeight: 500,
                        }}>
                            Customer Name <span className="text-red-500">*</span>
                        </span>
                    }
                    name="customer_name"
                    required={false}
                    rules={[{ required: true, message: "Required" }]}
                >
                    <Select
                        size="large"
                        loading={loadingCustomers}
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                            String(option?.label || "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        onSearch={handleCustomerSearch}
                        placeholder="Search and Select Customer"
                        className="CustomSelect"
                        options={customerOptions}
                        notFoundContent={
                            loadingCustomers ? "Searching..." : "No customers found"
                        }
                        allowClear
                        onChange={(customerId) => {
                            const selectedCustomer = customers.find(
                                (c: any) => c._id === customerId
                            );

                            const relatedProject = projects.find(
                                (p: any) => p.clientId?._id === customerId
                            );

                            const updateValues: any = {
                                customer_contact_number: selectedCustomer?.mobile || "",
                            };

                            if (relatedProject?._id) {
                                updateValues.project_name = relatedProject._id;
                            }

                            form.setFieldsValue(updateValues);
                        }}
                    />
                </Form.Item>

                <Form.Item
                    label={
                        <span style={{
                            color: '#4b5563',
                            fontFamily: 'inter, sans-serif',
                            fontSize: '14px',
                            paddingLeft: "15px",
                            fontWeight: 500,
                        }}>
                            Contact Number
                        </span>
                    }
                    name="customer_contact_number"
                    validateTrigger="onChange"
                    rules={[
                        {
                            pattern: /^[0-9]{10}$/,
                            message: "Please enter a valid 10-digit number",
                        },
                    ]}
                >
                    <Input placeholder="Enter contact number" className="Allinput text-[14px]" />
                </Form.Item>

                <Form.Item
                    label={
                        <span style={{
                            color: '#4b5563',
                            fontFamily: 'inter, sans-serif',
                            fontSize: '14px',
                            paddingLeft: "15px",
                            fontWeight: 500,
                        }}>
                            Service Type <span className="text-red-500">*</span>
                        </span>
                    }
                    name="service_type"
                    required={false}
                    rules={[{ required: true, message: "Required" }]}
                >
                    <Select
                        size="large"
                        placeholder="Select Service Type"
                        className="CustomSelect"
                    >
                        <Select.Option value="Hosting+domain">Hosting+Domain</Select.Option>
                        <Select.Option value="Hosting">Hosting</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    label={
                        <span style={{
                            color: '#4b5563',
                            fontFamily: 'inter, sans-serif',
                            fontSize: '14px',
                            paddingLeft: "15px",
                            fontWeight: 500,
                        }}>
                            Start Date <span className="text-red-500">*</span>
                        </span>
                    }
                    name="start_date"
                    required={false}
                    rules={[{ required: true, message: "Required" }]}
                >
                    <DatePicker
                        format="YYYY-MM-DD"
                        className="Allinput text-[14px]"
                        style={{ width: "100%" }}
                    />
                </Form.Item>

                <Form.Item
                    label={
                        <span style={{
                            color: '#4b5563',
                            fontFamily: 'inter, sans-serif',
                            fontSize: '14px',
                            paddingLeft: "15px",
                            fontWeight: 500,
                        }}>
                            End Date <span className="text-red-500">*</span>
                        </span>
                    }
                    name="end_date"
                    required={false}
                    rules={[{ required: true, message: "Required" }]}
                >
                    <DatePicker
                        format="YYYY-MM-DD"
                        className="Allinput text-[14px]"
                        style={{ width: "100%" }}
                    />
                </Form.Item>
            </div>

            <Row justify="end">
                <Col
                    xs={24}
                    sm={24}
                    md={24}
                    xl={24}
                    xxl={20}
                    className="d-flex justify-content-end me-4"
                >
                    <div className="button-wrapper" style={{ marginTop: 20, display: "flex", gap: 10 }}>
                        <button
                            className="allcanclebutton"
                            onClick={onClose}
                            type="button"
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button
                            className="allsubmitebutton w-20"
                            type="submit"
                            disabled={isLoading}
                        >
                            {isLoading
                                ? editData
                                    ? "Updating..."
                                    : "Saving..."
                                : editData
                                ? "Update"
                                : "Save"}
                        </button>
                    </div>
                </Col>
            </Row>
        </Form>
    </Modal>
);
};

export default AmcMaintenanceModal;
